import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Label } from '../ui/label';

const fade = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.25 },
};

const ARRIVAL_WINDOWS = [
  { id: 'morning', label: 'Morning', time: '8:00 AM – 11:00 AM', start: '08:00', end: '11:00' },
  { id: 'midday', label: 'Midday', time: '11:00 AM – 2:00 PM', start: '11:00', end: '14:00' },
  { id: 'afternoon', label: 'Afternoon', time: '2:00 PM – 5:00 PM', start: '14:00', end: '17:00' },
];

export const ScheduleStep = ({
  selectedDate,
  setSelectedDate,
  selectedWindow,
  setSelectedWindow,
  disabledDates,
  slotsLoading,
}) => (
  <motion.div
    key="s3"
    {...fade}
    className="bg-white rounded-2xl p-6 shadow-sm border border-zinc-100"
    data-testid="step-schedule"
  >
    <h2 className="text-lg sm:text-xl font-bold text-zinc-900 font-heading mb-1">
      Pick Your Date &amp; Time
    </h2>
    <p className="text-sm text-zinc-500 mb-5">Choose a time window that works best</p>

    <div className="grid md:grid-cols-2 gap-5">
      <div>
        <Label className="text-sm font-semibold text-zinc-700 mb-3 block">Select Date</Label>
        <div className="border border-zinc-200 rounded-xl p-1 inline-block" data-testid="date-calendar">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(d) => {
              setSelectedDate(d);
              setSelectedWindow(null);
            }}
            disabled={disabledDates}
            className="rounded-xl"
          />
        </div>
        {selectedDate && (
          <p className="mt-2 text-sm text-zinc-600">
            Selected: <strong>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</strong>
          </p>
        )}
      </div>

      <div>
        <Label className="text-sm font-semibold text-zinc-700 mb-3 block">Arrival Window</Label>
        <div className="space-y-3" data-testid="arrival-windows">
          {ARRIVAL_WINDOWS.map((w) => {
            const isActive = selectedWindow?.id === w.id;
            return (
              <button
                key={w.id}
                onClick={() => setSelectedWindow(w)}
                disabled={!selectedDate}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                  !selectedDate
                    ? 'border-zinc-100 bg-zinc-50 opacity-50 cursor-not-allowed'
                    : isActive
                      ? 'border-[#F56F05] bg-[#FFF7ED]'
                      : 'border-zinc-100 hover:border-[#F56F05]/40'
                }`}
                data-testid={`window-${w.id}`}
              >
                <div>
                  <p className="font-semibold text-zinc-900">{w.label}</p>
                  <p className="text-sm text-zinc-500">{w.time}</p>
                </div>
                {isActive && <CheckCircle2 className="h-5 w-5 text-[#F56F05]" />}
              </button>
            );
          })}
        </div>
        {slotsLoading && (
          <div className="mt-3 flex items-center gap-2 text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking availability...
          </div>
        )}
        <p className="text-xs text-zinc-400 mt-3">Available Monday – Saturday</p>
      </div>
    </div>
  </motion.div>
);

export { ARRIVAL_WINDOWS };
